import * as React from 'react'
import { GoogleMap, CircleProps } from 'react-google-maps'
import {
  ICoordinates,
  IGetGoogleMapPolyline,
  IMarker,
  IActiveTripResponseV2,
  IZoneObject,
  IOSMDirectionReq,
  IServiceDesignatedHotspot
} from '../types'
import { ICoordinatesWithShortKeyDTO } from '../types/google-map'
import ServiceDesignatedHotspotGoogleMapComponent from './google-map/ServiceDesignatedHotspotGoogleMap'

interface IStrategy {
  toFind: (stage: string) => boolean
  renderConditions: IRenderConditions
}

export interface IRenderConditions {
  isShowOrigin?: boolean
  isShowDestination?: boolean
  isShowCars?: boolean
  isShowCircle?: boolean
  isShowPolyline?: boolean
}

export const mapViewStrategyStages = {
  showInit: 'SHOW_INIT',
  showPolyline: 'SHOW_POLYLINE',
  liveTrip: 'LIVE_TRIP',
  showNearbyDrivers: 'SHOW_NEARBY_DRIVERS'
}

const strategy: IStrategy[] = [
  {
    toFind: (stage: string) => stage === mapViewStrategyStages.showInit,
    renderConditions: {}
  },
  {
    toFind: (stage: string) => stage === mapViewStrategyStages.showPolyline,
    renderConditions: {
      isShowOrigin: true,
      isShowDestination: true,
      isShowPolyline: true
    }
  },
  {
    toFind: (stage: string) => stage === mapViewStrategyStages.liveTrip,
    renderConditions: {
      isShowOrigin: true,
      isShowDestination: true,
      isShowCars: true,
      isShowPolyline: true
    }
  },
  {
    toFind: (stage: string) => stage === mapViewStrategyStages.showNearbyDrivers,
    renderConditions: {
      isShowOrigin: true,
      isShowDestination: true,
      isShowCars: true,
      isShowPolyline: true
    }
  }
]

interface IMapViewProps {
  renderStrategyStage: string
  origin: ICoordinates | null
  destination: ICoordinates | null
  location: IServiceDesignatedHotspot
  locationType?: string | undefined
  center?: ICoordinates
  zoom: number
  markers?: IMarker[]
  polygon?: ICoordinates[] | null
  zone?: IZoneObject | null
  zoneId?: string
  polyline: string | null
  bounds: any
  isDraggable: boolean
  isModal?: boolean
  notification?: any
  currentTrip?: IActiveTripResponseV2
  circles?: CircleProps[]
  getDirections?: (dto: IGetGoogleMapPolyline) => void
  selectPickup?: (locationInfo: ICoordinatesWithShortKeyDTO) => void
  selectDropoff?: (locationInfo: ICoordinatesWithShortKeyDTO) => void
  selectLocation?: (locationInfo: ICoordinatesWithShortKeyDTO, event: google.maps.MouseEvent) => void
  setPolyline?: (polyline: string) => void
  setBounds?: (bounds: any) => void
  zones: { [key: string]: IZoneObject } | {}

  getOSMDirection?: (dto: IOSMDirectionReq) => void
}

interface IMapViewState {
  decodedPolyline: any | null
  allowBoundFitting: boolean
}

class ServiceDesignatedHotspotMapView extends React.Component<IMapViewProps, IMapViewState> {
  public map: GoogleMap

  constructor(props: any) {
    super(props)
    this.state = {
      decodedPolyline: null,
      allowBoundFitting: true
    }
  }

  public componentDidUpdate(prevProps: IMapViewProps) {
    if (!this.props.isDraggable) {
      if (
        this.props.currentTrip &&
        this.props.currentTrip.routePolyline &&
        this.props.setPolyline &&
        this.props.currentTrip.routePolyline !== prevProps.polyline
      ) {
        this.props.setPolyline(this.props.currentTrip.routePolyline)
      }
      if (
        this.props.currentTrip &&
        this.props.currentTrip.bounds &&
        this.props.setBounds &&
        JSON.stringify(this.props.currentTrip.bounds) !== JSON.stringify(prevProps.bounds)
      ) {
        this.props.setBounds(this.props.currentTrip.bounds)
      }
    }
    if (
      this.props.isDraggable ||
      (!this.props.isDraggable && this.props.currentTrip && !this.props.currentTrip.routePolyline)
    ) {
      if (
        this.map &&
        (JSON.stringify(this.props.origin) !== JSON.stringify(prevProps.origin) ||
          JSON.stringify(this.props.destination) !== JSON.stringify(prevProps.destination))
      ) {
        if (this.props.origin && this.props.destination && this.props.getDirections) {
          this.props.getDirections({ origin: this.props.origin, destination: this.props.destination })
          if (this.props.getOSMDirection) {
            this.props.getOSMDirection({
              location: [
                [this.props.origin.lng, this.props.origin.lat],
                [this.props.destination.lng, this.props.destination.lat]
              ]
            })
          }
        }
      }
    }
    if (this.props.polyline && prevProps.polyline !== this.props.polyline) {
      const decodedPolyline = google.maps.geometry.encoding.decodePath(this.props.polyline.toString())
      this.setState({ decodedPolyline })
    }
    if (prevProps.polyline && !this.props.polyline) {
      this.setState({ decodedPolyline: null })
    }
    if (
      (!this.props.markers || !this.props.markers.length) &&
      this.props.bounds &&
      JSON.stringify(prevProps.bounds) !== JSON.stringify(this.props.bounds)
    ) {
      this.state.allowBoundFitting ? this.map.fitBounds(this.props.bounds) : this.setState({ allowBoundFitting: true })
    }
    if (this.props.markers && JSON.stringify(prevProps.markers) !== JSON.stringify(this.props.markers)) {
      this.setState({ allowBoundFitting: true })
    }
    if (prevProps.origin && this.props.origin && prevProps.destination && this.props.destination) {
      if (
        (prevProps.origin.lat !== this.props.origin.lat && prevProps.origin.lng !== this.props.origin.lng) ||
        (prevProps.destination.lat !== this.props.destination.lat &&
          prevProps.destination.lng !== this.props.destination.lng)
      ) {
        this.fitMarkerBounds(this.props.markers, this.state.decodedPolyline, true, true)
      }
    }
  }
  public render() {
    const renderStrategy = strategy.find(it => it.toFind(this.props.renderStrategyStage))

    return (
      <span className='w-100 relative'>
        <ServiceDesignatedHotspotGoogleMapComponent
          renderStrategy={renderStrategy ? renderStrategy.renderConditions : {}}
          containerElement={<div className='MapContainer' />}
          mapElement={<div className='GoogleMap' />}
          center={this.props.origin || this.props.center}
          onMapMounted={this.handleMapMounted}
          origin={this.props.origin}
          destination={this.props.destination}
          location={this.props.location}
          locationType={this.props.locationType}
          zoom={this.props.zoom}
          zone={this.props.zone}
          zones={this.props.zones}
          zoneId={this.props.zoneId}
          markers={this.props.markers}
          polygon={this.props.polygon}
          circles={this.props.circles}
          decodedPolyline={this.state.decodedPolyline}
          isDraggable={true}
          isModal={this.props.isModal}
          selectLocation={this.onSelectLocation}
          onOriginPinChanged={this.onOriginPinChanged}
          onDestPinChanged={this.onDestPinChanged}
          currentTrip={this.props.currentTrip}
        />
        {this.props.markers && this.state.allowBoundFitting && (
          <button className='absolute left-1 bottom-1 mb3 btn' onClick={this.handleMapRefresh}>
            Refresh map
          </button>
        )}
      </span>
    )
  }

  public handleMapRefresh = () => {
    if (this.props.markers) {
      this.fitMarkerBounds(this.props.markers, this.state.decodedPolyline)
    }
  }

  public handleMapMounted = (map: any) => {
    this.map = map
  }

  private onSelectLocation = (event: google.maps.MouseEvent) => {
    if (!this.props.selectLocation) {
      return
    }
    console.log('EVENTS: ', event)
    const coordinates = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    }

    this.props.selectLocation(coordinates, event)
  }

  private onOriginPinChanged = (event: google.maps.MouseEvent) => {
    if (!this.props.selectPickup) {
      return
    }

    const coordinates = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    }
    this.setState({ allowBoundFitting: false })
    this.props.selectPickup(coordinates)
  }

  private onDestPinChanged = (event: google.maps.MouseEvent) => {
    if (!this.props.selectDropoff) {
      return
    }
    const coordinates = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    }
    this.setState({ allowBoundFitting: false })
    this.props.selectDropoff(coordinates)
  }

  private fitMarkerBounds = (
    markers?: ICoordinates[],
    polyline?: any,
    ignoreCarMarker?: boolean,
    ignoreZoomLogic?: boolean
  ) => {
    if (!markers || !markers.length) {
      return
    }
    const bounds = new window.google.maps.LatLngBounds()
    let makerArray = markers
    if (ignoreCarMarker === true) {
      makerArray = markers.filter(item => item.type !== 'car')
    }
    makerArray.forEach(m => {
      bounds.extend(m)
    })

    if (polyline) {
      polyline.forEach((p: any) => bounds.extend(p))
    }

    // Don't zoom in too far on only one marker
    if (bounds.getNorthEast().equals(bounds.getSouthWest()) && ignoreZoomLogic !== true) {
      const extendPoint1 = new google.maps.LatLng(
        bounds.getNorthEast().lat() + 0.01,
        bounds.getNorthEast().lng() + 0.01
      )
      const extendPoint2 = new google.maps.LatLng(
        bounds.getNorthEast().lat() - 0.01,
        bounds.getNorthEast().lng() - 0.01
      )
      bounds.extend(extendPoint1)
      bounds.extend(extendPoint2)
    }
    this.map.fitBounds(bounds)
  }
}

export default ServiceDesignatedHotspotMapView



// WEBPACK FOOTER //
// ./src/components/ServiceDesignatedHotspotMapView.tsx